import React, { useState, useMemo, useContext } from 'react';
import Stack from '@mui/material/Stack';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';

import {
  BrandDarkBlue,
  CardContainer,
  TextHeader,
  CardTitle,
  useSnackbar,
  IOTTableWrapper,
  RegularText,
} from '@Iot-Bee/standard-web-library';

import DeviceDialog from './components/DeviceDialog';

import { noCacheFetcher } from '../../utils/fetch';
import { Device } from '../../Types/types';
import { Box } from '@mui/material';
import { DevicesContext } from '../../context/DevicesContext';

export function DevicesPage() {
  const navigate = useNavigate();

  const devicesContext = useContext(DevicesContext);

  if (!devicesContext) {
    throw new Error('DevicesContext is not defined');
  }

  const { devices, devicesAreLoading, devicesError, refreshDevices } = devicesContext;

  const [rowIdsSelected, setRowIdsSelected] = useState<string[]>([]);
  const [deviceToEdit, setDeviceToEdit] = useState<Device | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [tableOrdering, setTableOrdering] = useState<{ column: string; direction: 'asc' | 'desc' }>({
    column: '',
    direction: 'asc',
  });

  const sortedDevices = useMemo(() => {
    if (!devices) {
      return [];
    }

    const sortedDevices = [...devices].sort((a, b) => {
      const key = tableOrdering.column as keyof Device;
      if (tableOrdering.direction === 'asc') {
        return a[key] > b[key] ? 1 : -1;
      } else {
        return a[key] < b[key] ? 1 : -1;
      }
    });

    return sortedDevices;
  }, [devices, tableOrdering]);

  const {
    actions: { openSnackbar },
  } = useSnackbar();

  const columnHeaderClick = (column: string) => {
    const direction = tableOrdering.column === column && tableOrdering.direction === 'asc' ? 'desc' : 'asc';
    setTableOrdering({ column, direction });
  };

  // id is used to fetch the data from the object that we use
  // title is the title of the column/the displayed text
  // width is the width of the column (not used)
  // onClick is the function that is called when the column is clicked
  // priority is the priority of the column, for mobile table, depending on screensize the amount of priorities are displayed
  const columns = [
    {
      id: 'deviceName',
      title: 'Device',
      width: 250,
      onClick: (device: Device) => {
        navigate(`/devices/${device.id}`);
      },
      priority: 1,
      isKey: true,
      columnHeaderClick,
    },
    {
      id: 'status',
      title: 'Status',
      width: 200,
      priority: 2,
      columnHeaderClick,
    },

    {
      id: 'dataUsage',
      title: 'Data Usage',
      width: 200,
      priority: 3,
      columnHeaderClick,
    },
    {
      id: 'id',
      title: 'ID',
      width: 150,
      priority: 4,
      columnHeaderClick,
    },
    {
      id: 'tags',
      title: 'Tags',
      width: 300,
      priority: 4,
    },
  ];

  const handleDeviceUpdate = (deviceData: Device) => {
    const { deviceName, id, tags } = deviceData;
    noCacheFetcher(`${process.env.REACT_APP_API_URL}updatedevice`, {
      method: 'PUT',
      headers: new Headers({
        // Your header content
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        id: id,
        name: deviceName,
        tags: tags,
      }),
    })
      .then(() => {
        if (!devices) {
          return;
        }
        refreshDevices();
        openSnackbar('success', "Device's name updated successfully");
      })
      .catch((err) => {
        console.error(err);
        openSnackbar('error', "Couldn't update device's name");
      });
  };

  return (
    <CardContainer
      header={
        <Stack
          direction="row"
          justifyContent="space-between"
          alignContent="center"
          alignItems="center"
          sx={{
            bgcolor: rowIdsSelected.length > 0 ? BrandDarkBlue : '#ffffff',
            flexGrow: '1',
            height: '100%',
            padding: '0 20px',
            borderRadius: '6px',
          }}
        >
          {rowIdsSelected.length > 0 ? (
            <>
              <TextHeader sx={{ color: '#fff' }}>{rowIdsSelected.length} selected</TextHeader>
              <EditIcon sx={{ color: '#fff', cursor: 'pointer' }} />
            </>
          ) : (
            <>
              <CardTitle>Devices</CardTitle>
            </>
          )}
        </Stack>
      }
    >
      {devicesError ? (
        <Box sx={{ width: '100%', minHeight: '200px', position: 'relative' }}>
          <RegularText sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            Something went wrong getting the devices
          </RegularText>
        </Box>
      ) : (
        <>
          <IOTTableWrapper
            columns={columns}
            data={sortedDevices}
            rowIdsSelected={rowIdsSelected}
            setRowIdsSelected={setRowIdsSelected}
            withCheckboxes
            onEdit={(device) => {
              setDeviceToEdit(device);
              setDialogOpen(true);
            }}
            isLoading={devicesAreLoading}
            tableOrdering={tableOrdering}
          />
          {dialogOpen && (
            <DeviceDialog
              device={deviceToEdit}
              open={dialogOpen}
              onClose={() => {
                setDialogOpen(false);
              }}
              onSave={(e) => handleDeviceUpdate(e)}
            />
          )}
        </>
      )}
    </CardContainer>
  );
}

export default DevicesPage;
